/**
 * Analytics consent decision (S3 — Loyalty & Marketing Revamp, Block 3).
 *
 * Tracks whether the visitor has opted in to analytics-grade attribution
 * persistence, and whether they've dismissed the consent banner. This is the
 * tiny on-disk record of the *decision*; the attribution payload itself lives
 * in `attribution.ts` and is only persisted when consent is `true`.
 *
 * State machine:
 *   - `null`     → no decision yet (banner may show on tracked-link landings).
 *   - `"granted"`→ opted in; attribution persists to localStorage.
 *   - `"denied"` → opted out / dismissed; attribution stays session-only.
 */

const CONSENT_KEY = "tappya.consent.analytics";

export type ConsentDecision = "granted" | "denied" | null;

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The stored decision, or `null` if the user hasn't chosen yet. */
export function getConsentDecision(): ConsentDecision {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(CONSENT_KEY);
    if (raw === "granted" || raw === "denied") return raw;
    return null;
  } catch {
    return null;
  }
}

/** Boolean view used to seed `attribution.setAnalyticsConsent()`. */
export function readAnalyticsConsent(): boolean {
  return getConsentDecision() === "granted";
}

/** Persist the user's decision. */
export function setConsentDecision(decision: Exclude<ConsentDecision, null>): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(CONSENT_KEY, decision);
  } catch {
    /* private mode / quota — degrade silently */
  }
}

/** True only when no decision has been made yet. */
export function consentUndecided(): boolean {
  return getConsentDecision() === null;
}
